import clsx from 'clsx';
import { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { Tag } from 'react-feather';

import { ImageSize, ProductWidget, getProductImage, getProductImageAlt } from '@vestico/model';

import { Spinner, SpinnerPositioning, SpinnerSize } from '@vestico/shared';

import style from './ProductImage.module.css';

export interface ProductImageProps {
  product: ProductWidget | null | undefined;
  size: ImageSize;
}

export const ProductImage = ({ product, size }: ProductImageProps) => {
  const [imageHasError, setImageHasError] = useState(false);
  const [didLoadImage, setDidLoadImage] = useState(false);

  useLayoutEffect(() => {
    setImageHasError(false);
    setDidLoadImage(false);
  }, [product]);

  const onImageError = useCallback(() => {
    setImageHasError(true);
  }, []);

  const onImageLoad = useCallback(() => {
    setDidLoadImage(true);
  }, []);

  const spinnerMarkup = useMemo(() => {
    if (imageHasError) {
      return null;
    }

    if (!product || !didLoadImage) {
      return (
        <div className={clsx(style.spinnerContainer, style.container)}>
          <Spinner size={SpinnerSize.small} positioning={SpinnerPositioning.default} />
        </div>
      );
    }
    return null;
  }, [didLoadImage, imageHasError, product]);

  const contentMarkup = useMemo(() => {
    if (imageHasError) {
      return (
        <div className={clsx(style.placeholder, style.container)}>
          <Tag />
        </div>
      );
    }
    if (!product) {
      return spinnerMarkup;
    }

    return (
      <>
        {/* skipcq: JS-0760, JS-W1015  */}
        <img
          src={getProductImage(product, size)}
          alt={getProductImageAlt(product)}
          className={clsx(style.image, style.container)}
          onError={onImageError}
          onLoad={onImageLoad}
          draggable={false}
        />
        {spinnerMarkup}
      </>
    );
  }, [imageHasError, onImageError, onImageLoad, product, size, spinnerMarkup]);

  return contentMarkup;
};
